// ========================================
// pages/Login.js - User Login Page
// ========================================
// LAST MODIFIED: April 12, 2026
// CHANGES: 
// - Added email verification handling
// - Added Forgot Password link (single instance)
// - Using actual logo image from assets
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';

const Login = () => {
  const navigate = useNavigate();
  
  // State for form fields
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // States for email verification handling
  const [needsVerification, setNeedsVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resending, setResending] = useState(false);
  
  const location = useLocation();
  const [redirectMessage, setRedirectMessage] = useState(location.state?.message || '');

  useEffect(() => {
    if (redirectMessage) {
      const timer = setTimeout(() => setRedirectMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [redirectMessage]);

  // Handle input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear verification flag when user changes email
    if (needsVerification) {
      setNeedsVerification(false);
      setError('');
    }
  };

  // Resend verification email
  const handleResendVerification = async () => {
    setResending(true);
    setError('');
    
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/resend-verification-public`, {
        email: unverifiedEmail
      });
      
      setError('');
      alert(`Verification email sent to ${unverifiedEmail}. Please check your inbox.`);
      setNeedsVerification(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, formData);
      
      // Save token to localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      // Check if error is due to unverified email
      if (err.response?.data?.requiresVerification) {
        setNeedsVerification(true);
        setUnverifiedEmail(formData.email);
        setError('Please verify your email address before logging in.');
      } else {
        setError(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        {/* Logo - Using actual image asset */}
        <div style={styles.logoContainer}>
          <img 
            src="/images/symbols/logo-light.png" 
            alt="Agyapadie Vault" 
            style={styles.logoImage}
          />
        </div>

        <h2 style={styles.title}>Welcome Back</h2>
        <p style={styles.subtitle}>Log in to access your vault</p>

        {/* Redirect Message */}
        {redirectMessage && (
          <div style={styles.infoMessage}>
            🔐 {redirectMessage}
          </div>
        )}

        {/* Error Message */}
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Verification Required Box */}
        {needsVerification && (
          <div style={styles.verificationBox}>
            <p style={styles.verificationText}>
              <strong>📧 Email Not Verified</strong><br/>
              We sent a verification link to <strong>{unverifiedEmail}</strong> when you created your account.
              Please check your inbox (and spam folder) and click the verification link to activate your account.
            </p>
            <button 
              onClick={handleResendVerification} 
              style={styles.resendButton}
              disabled={resending}
            >
              {resending ? 'Sending...' : 'Resend Verification Email'}
            </button>
          </div>
        )}

        {/* Login Form - Hide when verification needed */}
        {!needsVerification && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                placeholder="Enter your email"
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                style={styles.input}
                placeholder="Enter your password"
                required
              />
            </div>
            
            {/* Forgot Password link */}
            <Link to="/forgot-password" style={styles.forgotLink}>
              Forgot password?
            </Link>

            <button 
              type="submit" 
              style={styles.loginButton}
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>
        )}

        {/* Links - Hide when verification needed */}
        {!needsVerification && (
          <div style={styles.links}>
            <Link to="/register" style={styles.link}>
              Don't have an account? Sign up
            </Link>
          </div>
        )}

        {/* Back to Home */}
        <Link to="/" style={styles.backLink}>
          ← Back to Home
        </Link>
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A1A1A',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px'
  },
  loginBox: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '10px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
  },
  logoContainer: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  logoImage: {
    height: '50px',
    width: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: '10px',
    textAlign: 'center'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '30px',
    textAlign: 'center'
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #ef4444',
    color: '#dc2626',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px',
    fontSize: '14px',
    textAlign: 'center'
  },
  infoMessage: {
    backgroundColor: '#E3F2FD',
    borderLeft: '4px solid #2196F3',
    color: '#0D47A1',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    textAlign: 'center'
  },
  verificationBox: {
    backgroundColor: '#FFF3E0',
    border: '1px solid #FF9800',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    textAlign: 'center'
  },
  verificationText: {
    fontSize: '14px',
    color: '#E65100',
    marginBottom: '15px',
    lineHeight: '1.5'
  },
  resendButton: {
    padding: '10px 20px',
    backgroundColor: '#FF9800',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1A1A1A'
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '14px',
    transition: 'border-color 0.3s',
    outline: 'none'
  },
  loginButton: {
    padding: '14px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
    marginTop: '10px'
  },
  links: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    marginTop: '20px'
  },
  link: {
    color: '#2E7D32',
    textDecoration: 'none',
    fontSize: '14px',
    cursor: 'pointer'
  },
  forgotLink: {
    color: '#2E7D32',
    textDecoration: 'none',
    fontSize: '12px',
    textAlign: 'right',
    display: 'block',
    marginTop: '5px'
  },
  backLink: {
    display: 'block',
    textAlign: 'center',
    marginTop: '20px',
    color: '#666',
    textDecoration: 'none',
    fontSize: '14px'
  }
};

export default Login;